import { useCallback, useState } from 'react';

export interface GeolocationResult {
  lat: number;
  lng: number;
  accuracy?: number;
}

export interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

const DEFAULT_OPTIONS: Required<UseGeolocationOptions> = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 60000,
};

/**
 * Geolocation helper — same options as the legacy WildfireForm:
 * enableHighAccuracy + 10s timeout + 60s cache. Returns a function the
 * caller invokes on a user gesture (button tap) so the browser surfaces
 * the permission prompt at the right moment.
 */
export function useGeolocation(opts: UseGeolocationOptions = {}) {
  const options = { ...DEFAULT_OPTIONS, ...opts };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capture = useCallback((): Promise<GeolocationResult | null> => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Geolocation is not available in this browser.');
      return Promise.resolve(null);
    }
    setLoading(true);
    setError(null);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLoading(false);
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        (err) => {
          setLoading(false);
          if (err.code === err.PERMISSION_DENIED) {
            setError('Location permission denied. Tap the map to set the location.');
          } else {
            setError('Could not get your location. Tap the map to set it manually.');
          }
          resolve(null);
        },
        options,
      );
    });
  }, [options.enableHighAccuracy, options.timeout, options.maximumAge]);

  return { capture, loading, error, setError };
}
